import { useEffect, type ReactNode } from 'react';
import { ArtifactWorkspace, Icon, icons, theme } from '@agentisai/design-system';
import type { TurnArtifact } from '@agentisai/shared-types';
import { formatArtifactSize, formatArtifactType } from '../lib/formatArtifactMeta';
import { ApiError } from '../queries/client';
import { useArtifacts } from '../queries/useArtifacts';
import { useGetArtifact } from '../queries/useGetArtifact';
import { ArtifactPreview } from './ArtifactPreview';

const muted = {
  padding: theme.space[5],
  margin: 0,
  color: theme.color.neutral.muted,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

const downloadLink = {
  color: theme.color.accent.blue[700],
  font: 'inherit',
} as const;

export interface FileViewPanelProps {
  /** The workstream the files belong to — scopes the reads (the BFF is the tenant gate). */
  readonly workstreamId: string;
  /** The open file tabs (opaque ids), in tab order (the `?files=` selection). */
  readonly openFileIds: readonly string[];
  /** The active tab (the `?file=` selection) — always one of {@link openFileIds}. */
  readonly activeFileId: string;
  /** Activate a tab (click). */
  readonly onSelect: (artifactId: string) => void;
  /** Close a tab (the ×). */
  readonly onClose: (artifactId: string) => void;
  /** Prune the open set to these still-valid ids (self-heal, single-pass). */
  readonly onPrune: (validIds: readonly string[]) => void;
}

/**
 * FileViewPanel — the S11 View flow, multi-tab. Each file the user Views opens as a
 * tab in the centre `ArtifactWorkspace` (the same chrome agent deliverables use);
 * the active tab renders via the existing {@link ArtifactPreview} — text-family files
 * inline (markdown / code / HTML in a sandboxed iframe), a binary as a note + a
 * Download to its freshly-presigned `url`.
 *
 * Tab labels come from the S09 list (`useArtifacts`, cached + shared with the rail);
 * the active tab's `content`/`url` come from `getArtifact` (re-presigned on demand,
 * ledger URLs expire ~1 h). When files leave the list — deleted while open (the S10
 * "clear the centre pane" case, reached because the delete invalidates these queries by
 * key prefix) or stale ids from another workstream — the tabs self-heal shut in one
 * pass via `onPrune`; a 404 on the active tab renders blank (not an error) while that
 * prune lands, so a delete-while-viewing doesn't flash an error.
 */
export function FileViewPanel({
  workstreamId,
  openFileIds,
  activeFileId,
  onSelect,
  onClose,
  onPrune,
}: FileViewPanelProps) {
  const list = useArtifacts(workstreamId);
  const content = useGetArtifact(workstreamId, activeFileId);
  const data = content.data?.data;
  const activeGone =
    content.isError && content.error instanceof ApiError && content.error.status === 404;

  // Self-heal: once the list has loaded, prune every open tab whose file is no longer
  // in it (deleted / stale) in ONE update. Navigation is a side effect → an effect.
  const listArtifacts = list.data?.data.artifacts;
  const openKey = openFileIds.join(',');
  useEffect(() => {
    if (!listArtifacts) return;
    const known = new Set(listArtifacts.map((a) => a.artifactId));
    const ids = openKey ? openKey.split(',') : [];
    const valid = ids.filter((id) => known.has(id));
    if (valid.length !== ids.length) onPrune(valid);
  }, [listArtifacts, openKey, onPrune]);

  const byId = new Map((listArtifacts ?? []).map((a) => [a.artifactId, a]));
  const tabs = openFileIds.map((id) => ({
    id,
    label: byId.get(id)?.filename ?? 'Loading…',
    icon: <Icon as={icons.FileText} size={14} />,
  }));

  // Body — the ACTIVE tab's content.
  let body: ReactNode;
  let metaLeft: ReactNode = null;
  let metaRight: ReactNode = null;
  if (content.isPending) {
    body = <p style={muted}>Loading preview…</p>;
  } else if (activeGone) {
    // Deleted / stale — the self-heal effect prunes this tab; render nothing meanwhile.
    body = null;
  } else if (content.isError || !data) {
    body = <p style={muted}>Couldn&rsquo;t open this file. Please try again.</p>;
  } else {
    metaLeft = (
      <>
        {formatArtifactType(data.contentType)} · {formatArtifactSize(data.sizeBytes)}
      </>
    );
    metaRight = data.url ? (
      <a href={data.url} download={data.filename} style={downloadLink}>
        Download
      </a>
    ) : null;
    // Map the fetched artifact onto the shape ArtifactPreview renders (content →
    // preview, the opaque id → the identity key).
    const previewArtifact: TurnArtifact = {
      filename: data.filename,
      contentType: data.contentType,
      sizeBytes: data.sizeBytes,
      key: data.artifactId,
      url: data.url,
      preview: data.content,
    };
    body = data.content ? (
      <ArtifactPreview artifact={previewArtifact} />
    ) : (
      <p style={muted}>
        No inline preview for this file type{data.url ? ' — use Download to open it.' : '.'}
      </p>
    );
  }

  return (
    <ArtifactWorkspace
      tabs={tabs}
      activeId={activeFileId}
      onSelect={onSelect}
      onClose={onClose}
      metaLeft={metaLeft}
      metaRight={metaRight}
    >
      {body}
    </ArtifactWorkspace>
  );
}
FileViewPanel.displayName = 'FileViewPanel';
